/**
 * Module download API functions for the GDB Cloud Edition.
 *
 * Calls the existing Rust/Axum endpoint:
 *   GET /api/modules/:id/download?ueVersion=5.7 -> ModuleDownloadResponse
 *
 * Uses the shared api-client which auto-attaches Bearer tokens.
 */

import { api } from '@/lib/api-client'
import type { ModuleDownloadResponse } from './types'

/** Sidecar's view of whether a module's primary DLL is on disk. */
export interface InstalledStatus {
  moduleId: string
  installed: boolean
}

/**
 * Ask the sidecar for the list of UE sibling plugins installed under
 * `Plugins/GDB/` in the current project. Phase 19-07 reconciliation
 * source: ActivityBar / EmbedMain call this on mount and prune
 * `activatedModulesAtom` of any hybrid modules whose folder isn't
 * actually on disk (stale localStorage from a different UE project's
 * WebView2 user data, etc.).
 *
 * Returns `{ modules: [] }` when the sidecar is unreachable or the
 * response is malformed — callers must NOT treat that as "everything
 * stale, wipe atom" because a temporary network blip would then erase
 * the user's activations. The caller's reconciliation logic should
 * skip pruning entirely on an empty/error response and only act on a
 * successful list.
 */
export async function getInstalledModules(): Promise<{ modules: InstalledStatus[]; ok: boolean }> {
  const response = await api.get('/api/modules/installed')
  if (!response.ok) {
    return { modules: [], ok: false }
  }
  // Sidecar returns the standard ModuleResponse envelope:
  //   { success: bool, data: InstalledModuleInfo[], error?: string }
  // where InstalledModuleInfo is { moduleId: string, installed: bool }.
  const envelope = (await response.json().catch(() => null)) as
    | { success?: boolean; data?: Array<{ moduleId?: string; installed?: boolean }> }
    | null
  if (!envelope || envelope.success !== true || !Array.isArray(envelope.data)) {
    return { modules: [], ok: false }
  }
  const modules: InstalledStatus[] = envelope.data
    .filter((m): m is { moduleId: string; installed?: boolean } => typeof m?.moduleId === 'string')
    .map((m) => ({ moduleId: m.moduleId, installed: m.installed !== false }))
  return { modules, ok: true }
}

/**
 * Ask the sidecar whether the module's DLL exists in
 * {Plugin}/Binaries/Win64/. Cheap local call. The Activate UI uses
 * this as the authoritative "is installed" signal — the local
 * `activatedModulesAtom` is a UI persistence cache that can drift
 * from filesystem reality (the user may have wiped Binaries, or
 * never actually completed an install in this session). Sidecar
 * filesystem is truth.
 */
export async function getModuleInstalled(moduleId: string): Promise<InstalledStatus> {
  const response = await api.get(
    `/api/local-modules/${encodeURIComponent(moduleId)}/installed`
  )
  if (!response.ok) {
    // Treat sidecar errors as "not installed" so the UI shows the
    // Activate button — better to offer a no-op install than to
    // promise an Open path that goes nowhere.
    return { moduleId, installed: false }
  }
  const data = (await response.json().catch(() => ({}))) as Partial<InstalledStatus> & { success?: boolean }
  return { moduleId, installed: !!data.installed }
}

/**
 * Fetch the presigned download URL for a module.
 * GET /api/modules/{moduleId}/download?ueVersion={ueVersion}
 * Requires Bearer JWT. Server re-checks entitlement from DB.
 *
 * Useful for introspection (what version would be installed). To actually
 * trigger the install use `installModule` below — it returns after the
 * sidecar has downloaded, extracted, and staged the DLLs, so the caller
 * knows when to prompt for a restart.
 */
export async function getModuleDownload(
  moduleId: string,
  ueVersion = '5.7'
): Promise<ModuleDownloadResponse> {
  const response = await api.get(
    `/api/modules/${encodeURIComponent(moduleId)}/download?ueVersion=${encodeURIComponent(ueVersion)}`
  )
  if (!response.ok) {
    const data: { error?: string } = await response.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to get module download')
  }
  return response.json() as Promise<ModuleDownloadResponse>
}

/** Shape returned by the sidecar's POST /api/modules/:id/download handler. */
export interface ModuleInstallStatus {
  moduleId: string
  success: boolean
  message: string
  version: string | null
  requiresRestart: boolean
}

interface ModuleInstallResponse {
  success: boolean
  data?: ModuleInstallStatus
  error?: string
}

/**
 * Activate a module: confirm entitlement against the cloud, then either
 * (a) skip the download because the module's DLL is already on disk
 * (the bundled-with-base-plugin case), or (b) download + extract +
 * stage it for promotion on next launch (the genuinely-external case).
 *
 * The two concerns are orthogonal:
 *   - Entitlement   → cloud (versions table + auth)
 *   - Installation  → sidecar (filesystem)
 *
 * Routing:
 *   GET  /api/modules/:id/download         → cloud (Vite proxy)
 *   GET  /api/local-modules/:id/installed  → sidecar (Vite proxy)
 *   POST /api/local-modules/install        → sidecar (Vite proxy)
 *
 * Today every shipping module lives in the base plugin zip, so the fast
 * lane (a) is the common case. When the catalog grows to genuinely
 * external modules (per-module CI uploads), the lane (b) code is
 * already in place and takes over without changes here.
 */
export async function installModule(
  moduleId: string,
  ueVersion = '5.7'
): Promise<ModuleInstallStatus> {
  // 1. Entitlement: cloud confirms the user owns the module and hands
  //    back a presigned URL for the install case.
  const urlResponse = await api.get(
    `/api/modules/${encodeURIComponent(moduleId)}/download?ueVersion=${encodeURIComponent(ueVersion)}`
  )
  if (!urlResponse.ok) {
    const data: { error?: string } = await urlResponse.json().catch(() => ({}))
    throw new Error(data.error || `Activation failed (${urlResponse.status})`)
  }
  const urlData = (await urlResponse.json()) as ModuleDownloadResponse
  if (!urlData.success || !urlData.module) {
    throw new Error('Cloud did not return module info')
  }

  // 2. Installation state: sidecar checks whether the DLL is already
  //    on disk (because the base plugin zip ships it bundled).
  const installedResp = await api.get(
    `/api/local-modules/${encodeURIComponent(moduleId)}/installed`
  )
  const installedRaw = await installedResp.text()
  let installedJson: { success?: boolean; installed?: boolean; error?: string } = {}
  try {
    installedJson = JSON.parse(installedRaw) as typeof installedJson
  } catch {
    // Sidecar unreachable / non-JSON — treat as "not installed" so we
    // fall through to the download path rather than silently activating
    // something that isn't actually present.
    installedJson = { installed: false }
  }
  const alreadyInstalled = installedResp.ok && installedJson.installed === true

  if (alreadyInstalled) {
    // Fast lane: DLL is already on disk from the base plugin install.
    // Nothing to download or stage; the activation is purely a UI-state
    // flip that the caller does after this resolves. The module is
    // already loaded if the user restarted UE since the install, so
    // no restart is required either.
    return {
      moduleId: urlData.module.id,
      success: true,
      message: `${urlData.module.name} activated`,
      version: urlData.module.version || null,
      requiresRestart: false,
    }
  }

  if (!urlData.module.downloadUrl) {
    throw new Error('Module is not installed locally and no download URL is available')
  }

  // 3. Slow lane: hand the presigned URL to the sidecar. It downloads,
  //    extracts the zip, and stages DLLs to
  //    {Plugin}/Resources/.pending_modules/{id}/. On next editor launch,
  //    `FGDB_PendingModuleInstaller::ApplyAll` (top of
  //    GDB_Core::StartupModule) promotes the staged binaries into
  //    Binaries/Win64/ before any downstream GDB module loads.
  const installResponse = await api.post('/api/local-modules/install', {
    moduleId: urlData.module.id,
    downloadUrl: urlData.module.downloadUrl,
    fileName: urlData.module.fileName,
  })

  // Body-as-text-then-parse so a non-JSON failure surfaces a useful
  // error rather than the opaque "response.json() threw."
  const rawBody = await installResponse.text()
  let installData: ModuleInstallResponse
  try {
    installData = JSON.parse(rawBody) as ModuleInstallResponse
  } catch {
    const preview = rawBody.slice(0, 200).replace(/\s+/g, ' ').trim()
    throw new Error(
      `Install endpoint returned non-JSON (HTTP ${installResponse.status}). Body preview: ${preview || '(empty)'}`
    )
  }
  if (!installResponse.ok || !installData.success || !installData.data) {
    throw new Error(installData.error || `Local install failed (${installResponse.status})`)
  }
  return installData.data
}
